import type { Column } from '../../../../../claim-history/simple-table'
import type { Procedure, CareTeamMember, ProcedureDetailsProps, ProcedureDetailsValues, ProcedureCreateTableFormProps, ProcedureUpdateTableFormProps, ProcedureFormState } from '../types'
import type { Dispatch, SetStateAction } from 'react'
import type { OptionProps } from 'react-select'

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { components } from 'react-select'
import AsyncCreatableSelect from 'react-select/async-creatable'

import http from '../../../../../../lib/http'
import { useAppSelector } from '../../../../../../store'
import * as Table from '../../../../../claim-history/simple-table'
import { useInvalidateIGAO } from '../../../../../igao/hooks/invalidate-igao'
import FriendlyDate from '../../../../../shared/Date/FriendlyDate'
import * as Grid from '../../../../../shared/grid'
import { MaterialDeleteButton } from '../../../../../shared/material-delete-button'
import { Button, Checkbox, PortedModal } from '../../../../../shared/NarusMaterial'

import { CareTeamMemberSelect } from './care-team-member-select'

type ProcedureQueryKeys = [
  string,
  string
]

interface Response {
  entries: Procedure[]
  empty: boolean
}

interface ModalState {
  open: boolean
  title: string
  actions: JSX.Element[]
}

interface ProcedureGridProps {
  createFormState: ProcedureFormState
  setCreateFormState: Dispatch<SetStateAction<ProcedureFormState>>
}

export function ProcedureGrid ({ createFormState, setCreateFormState }: ProcedureGridProps): JSX.Element {
  let patientId = useAppSelector(state => state.globalVars?.params?.id)
  let invalidateIGAO = useInvalidateIGAO('admissions')
  let queryClient = useQueryClient()
  let [ modal, setModal ] = useState<ModalState>()

  const query = useQuery<Response, unknown, Response, ProcedureQueryKeys>({
    queryKey: [
      'procedures-grid',
      patientId as string
    ],
    queryFn: async () => {
      async function getProcedures (): Promise<Response> {
        return await http.getRequest(`/patients/${patientId as string}/procedures`)
      }

      let response = await getProcedures()

      return {
        ...response,
        entries: response.entries.map((entry) => ({
          ...entry,
          physician: entry.physician
            ? Object.keys(entry.physician).length > 0
              ? entry.physician
              : null
            : null
        }))
      }
    },
    refetchOnWindowFocus: false

  })

  let isEmpty = query.data?.empty ?? false

  const refetch = useCallback((): void => {
    query.refetch()
      .catch((e) => {
        console.error(e)
      })
    invalidateIGAO()
      .catch((e) => {
        console.error(e)
      })

    queryClient.invalidateQueries({
      predicate ({ queryKey }) {
        return Array.isArray(queryKey) && queryKey[0] === 'claims-history/admissions'
      }
    })
      .catch(err => {
        console.error(err)
      })
  }, [ invalidateIGAO, query, queryClient ])

  const handleDeleteProcedure = useCallback(async (id: string): Promise<void> => {
    if (!patientId) {
      return
    }

    try {
      await http.deleteRequest(`/patients/${patientId}/procedure/${id}`)
      refetch()
    } catch (e) {
      console.error(e)
    }
  }, [ patientId, refetch ])

  const handleCreateProcedure = useCallback((value: ProcedureDetailsValues): void => {
    if (!patientId) {
      return
    }

    let promise = isEmpty
      ? http.putRequest(`/patients/${patientId}/procedures`, { empty: false })
      : Promise.resolve()

    promise.then(async () => await http.postRequest(`/patients/${patientId as string}/procedure`, { value }))
      .then(() => {
        refetch()
        setCreateFormState({
          open: false,
          form: undefined
        })
      }).catch((e: unknown) => {
        console.error(e)
      })
  }, [ patientId, refetch, setCreateFormState, isEmpty ])

  const handleUpdateProcedure = useCallback(async (id: string, value: ProcedureDetailsValues): Promise<void> => {
    if (!patientId) {
      return
    }

    http.putRequest(`/patients/${patientId}/procedure/${id}`, value)
      .then(() => {
        refetch()
      }).catch((e: unknown) => {
        console.error(e)
      })
  }, [ patientId, refetch ])

  const columns: Array<Column<Procedure>> = useMemo(() => [
    {
      name: 'type',
      header: 'Type',
      getValue: (x) => x.type ?? null
    }, {
      name: 'name',
      header: 'Name',
      getValue: (x) => x.name ?? null
    }, {
      name: 'physician',
      header: 'Physician',
      getValue: (x) => x?.physician?.display ?? null
    },
    {
      name: 'procedure_date',
      header: 'Procedure Date',
      getValue: (x) => (x.date ? <FriendlyDate date={x.date} /> : null)
    },
    {
      name: 'actions',
      header: 'Actions',
      headerStyle: {
        justifyContent: 'center'
      },
      style: {
        display: 'flex',
        justifyContent: 'center'
      },
      isAction: true,
      gridArea: '0.4fr',
      getValue: function Action (item) {
        return (
          <MaterialDeleteButton
            onClick={() => {
              setModal({
                title: 'Are you sure you want to delete this procedure?',
                open: true,
                actions: [
                  <Button
                    key={1}
                    data-cy='modal_cancel-btn'
                    label='Cancel'
                    // @ts-expect-error // It's fine
                    onClick={() => {
                      setModal(undefined)
                    }}
                    grey
                  />,
                  <Button
                    key={2}
                    label='Delete'
                    data-cy='modal_discard-btn'
                    // @ts-expect-error // It's fine
                    onClick={() => {
                      handleDeleteProcedure(item.id)
                        .catch((e) => {
                          console.error(e)
                        })

                      setModal(undefined)
                    }}
                  />
                ]
              })
            }}
            style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', cursor: 'pointer' }}
          />
        )
      }
    }], [ handleDeleteProcedure ])

  function handlePatientDeniesRecentProcedures (value: boolean): void {
    if (!patientId) return

    http.putRequest(`/patients/${patientId}/procedures`, { empty: value })
      .then(() => {
        refetch()
      }).catch((e: unknown) => {
        console.error(e)
      })
  }

  return (
    <>
      <div style={{ backgroundColor: '#fff', borderRadius: '1rem' }}>
        <Table.Table
          header={(
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                  <h2>Procedures</h2>
                  <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
                    <Checkbox
                      label='Patient denies any recent procedures'
                      value={query.data?.empty}
                      disabled={query.isLoading || (query.data?.entries?.length ?? 0) > 0}
                      onChange={(val: boolean) => handlePatientDeniesRecentProcedures(val)}
                      className={undefined}
                      labelClassName={undefined}
                    />
                  </div>
                </div>

                {!createFormState.open && (
                  <Grid.Button
                    onClick={() => setCreateFormState((prev) => ({ ...prev, open: true }))}
                    style={{ border: '1px solid #4D1EE6' }}
                    rounded
                  >
                    Add Procedure
                  </Grid.Button>
                )}
              </div>
              {createFormState.open && (
                <div style={{ padding: '4rem 3rem 1rem 0' }}>
                  <CreateTableForm
                    initialState={createFormState.form}
                    onSubmit={handleCreateProcedure}
                    onCancel={() => setCreateFormState(() => ({ open: false }))}
                  />
                </div>
              )}
            </div>
          )}
          noDataPlaceholder={query.data?.entries.length === 0 ? 'This patient has no procedures' : undefined}
          columns={columns}
          data={query.data?.entries ?? []}
          error={query.error as Error}
          queryStatus={query.status}
          fetchStatus={query.fetchStatus}
          expandingComponent={(item, _, setState) => (
            <UpdateTableForm
              onSubmit={(value) => {
                handleUpdateProcedure(item.id, value).then(() => {
                  setState(null)
                }).catch(e => {
                  console.error(e)
                })
              }}
              item={item}
              onCancel={() => setState(null)}
            />
          )}
          disableMinHeight
        />
      </div>

      <PortedModal
        open={modal?.open}
        title={modal?.title}
        actions={modal?.actions}
      />
    </>
  )
}

function OptionComponent ({ children, ...props }: OptionProps<Option, false>): JSX.Element {
  return (
    <components.Option {...props}>
      {props.data.__isNew__ && (
        <span>{props.data.label}</span>
      )}

      {!props.data.__isNew__ && (
        <span>
          <strong>{props.data.value}</strong> {props.data.label}
        </span>
      )}
    </components.Option>
  )
}

interface Option {
  __isNew__?: boolean
  value: string
  label: string
}

async function loadOptions (value: string): Promise<Option[]> {
  interface Entry {
    code: string
    description: string
  }

  let params: Record<string, unknown> = {}
  let normalizedSearch = value.trim().toLowerCase()

  if (normalizedSearch.length > 0) {
    params.search = normalizedSearch
  }

  let response: Entry[] = await http.getRequest('/cpt', params, { clientName: false })

  return response.map(({ code, description }) => ({
    value: code,
    label: description
  }))
}

const defaultValue = {
  type: '',
  name: '',
  comments: '',
  date: '',
  facility: '',
  physician: null,
  code: undefined,
  descriptor: undefined
}

export function Form ({ item, onSubmit, onCancel, initialState }: ProcedureDetailsProps): JSX.Element {
  let patientId = useAppSelector(state => state.globalVars?.params?.id) as string

  const [ values, setValues ] = useState<ProcedureDetailsValues>(defaultValue)
  const [ initialValues, setInitialValues ] = useState<ProcedureDetailsValues | undefined>(defaultValue)

  useEffect(() => {
    const mapped = initialState
      ? {
        type: initialState?.type ?? '',
        name: initialState?.name ?? '',
        comments: initialState?.comments ?? '',
        date: initialState?.date ?? '',
        facility: initialState?.facility ?? '',
        physician: initialState?.physician ?? null,
        code: initialState?.code ?? undefined,
        descriptor: initialState?.descriptor ?? undefined
      }
      : {
        type: item?.type ?? '',
        name: item?.name ?? '',
        comments: item?.comments ?? '',
        date: item?.date ?? '',
        facility: item?.facility ?? '',
        physician: item?.physician ?? null,
        code: (item?.code as string | undefined) ?? undefined,
        descriptor: (item?.descriptor as string | undefined) ?? undefined
      }

    setValues(mapped)
    setInitialValues(initialState ? undefined : mapped)
  }, [ initialState, item ])

  function handleChange (key: keyof ProcedureDetailsValues, value: string | undefined): void {
    setValues((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const { data: careTeamMembers } = useQuery<CareTeamMember[], unknown, CareTeamMember[], [string]>({
    queryKey: [ patientId ],
    queryFn: async () => {
      return await http.getRequest(`/patients/${patientId}/careNetwork`)
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })

  const saveButtonDisabled = (values.name ?? '').trim().length === 0 || _.isEqual(values, initialValues)

  return (
    <div
      className='procedure-form-wrapper'
      style={{ display: 'flex', gap: '2rem', flexDirection: 'column' }}
    >
      <div style={{ display: 'flex', gap: '2rem' }}>
        <div style={{ display: 'flex', flex: 2, flexDirection: 'column', gap: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr' }}>
            <Grid.SelectWrapper
              label='Procedure Name'
              data-test='procedure-name-select'
              required
            >
              <AsyncCreatableSelect
                loadOptions={loadOptions}
                styles={{
                  control: (styles: any, { isFocused }) => ({
                    ...styles,
                    backgroundColor: '#fff',
                    border: '1px solid #DADCE0',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    height: '2.6rem',

                    outline: isFocused ? '#5404FF solid 2px' : 'none'
                  }),
                  container: (styles: any, { isFocused }) => ({
                    ...styles,
                    flex: 1,
                    backgroundColor: '#F6F6F6',
                    boxShadow: 'none',
                    borderRadius: '8px',
                    zIndex: isFocused ? 10 : 5
                  }),
                  indicatorSeparator: (styles: any) => ({
                    ...styles,
                    marginBottom: 0,
                    marginTop: 0
                  }),
                  dropdownIndicator: (styles: any) => ({
                    ...styles,
                    padding: '0 18px'
                  })
                }}
                isClearable
                placeholder='Start typing to select from the drop down list'
                instanceId='procedure-name-select'
                value={
                  values.name
                    ? { value: values.name, label: values.name }
                    : null
                }
                onChange={value => {
                  if (!value || !('label' in value)) {
                    handleChange('name', '')
                    handleChange('code', undefined)
                    handleChange('descriptor', undefined)
                    return
                  }

                  handleChange('name', `${value.value} ${value.label}`)
                  handleChange('code', value.value)
                  handleChange('descriptor', value.label)
                  handleChange('type', getProcedureType(value.value) ?? '')
                }}
                allowCreateWhileLoading
                createOptionPosition='first'
                formatCreateLabel={value => `Use "${value}"`}
                onCreateOption={value => {
                  handleChange('name', value.trim())
                  handleChange('code', undefined)
                  handleChange('descriptor', undefined)
                }}
                components={{
                  DropdownIndicator: null,
                  Option: OptionComponent
                }}
              />
            </Grid.SelectWrapper>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: '2rem', columnGap: '0.9rem' }}>
            <Grid.Input
              placeholder='Type'
              value={values.type}
              onChange={value => handleChange('type', value)}
            />
            <Grid.Input
              type='date'
              placeholder='Date'
              value={values.date ?? ''}
              onChange={value => handleChange('date', value)}
            />
            <CareTeamMemberSelect
              placeholder='Physician'
              careTeamMembers={careTeamMembers}
              value={values.physician}
              onChange={(value) => {
                setValues((prev) => ({ ...prev, physician: value }))
              }}
              instanceId='procedure-physician-select'
            />
            <Grid.Input
              placeholder='Facility'
              value={values.facility ?? ''}
              onChange={value => handleChange('facility', value)}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flex: 1 }}>
          <Grid.Textarea
            placeholder='Comments'
            value={values.comments ?? ''}
            onChange={(e) => handleChange('comments', e.target.value)}
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '0.4rem'
        }}
      >
        <Grid.Button
          disabled={saveButtonDisabled}
          onClick={() => onSubmit(values)}
          style={{
            backgroundColor: '#EAE4FC',
            color: '#6D45F1',
            padding: '.8rem 3rem'
          }}
        >
          {item ? 'Save' : 'Create'}
        </Grid.Button>
        <Grid.Button
          onClick={onCancel}
          light
          style={{ padding: '.8rem 3rem' }}
        >
          Cancel
        </Grid.Button>
      </div>

    </div>
  )
}

export function CreateTableForm ({ initialState, onCancel, onSubmit }: ProcedureCreateTableFormProps): JSX.Element {
  return (
    <Form
      initialState={initialState}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}

export function UpdateTableForm ({ item, onCancel, onSubmit }: ProcedureUpdateTableFormProps): JSX.Element {
  return (
    <Form
      item={item}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}

export function getProcedureType (code: string): string | null {
  let pattern = /^[A-Z]\d{4,5}$/
  let parsed = parseInt(code, 10)

  if (!isNaN(parsed)) {
    switch (true) {
      case parsed >= 1000 && parsed <= 1999:
        return 'Anesthesia Services'

      case parsed >= 10021 && parsed <= 69990:
        return 'Surgery'

      case parsed >= 70010 && parsed <= 79999:
        return 'Radiology Services'

      case parsed >= 80047 && parsed <= 89398:
        return 'Pathology and Laboratory Services'

      case parsed >= 90281 && parsed <= 99607:
        return 'Medical Services and Procedures'

      case parsed >= 99202 && parsed <= 99499:
        return 'Evaluation & Management'
    }
  }

  if (pattern.test(code)) {
    switch (code.substring(0, 1)) {
      case 'A':
        return 'Transportation, Medical & Surgical Supplies, Miscellaneous & Experimental'

      case 'B':
        return 'Enteral and Parenteral Therapy'

      case 'C':
        return 'Temporary Hospital Outpatient Prospective Payment System'

      case 'D':
        return 'Dental Procedures'

      case 'E':
        return 'Durable Medical Equipment'

      case 'G':
        return 'Temporary Procedures & Professional Services'

      case 'H':
        return 'Rehabilitative Services'

      case 'J':
        return 'Drugs Administered Other Than Oral Method, Chemotherapy Drugs'

      case 'L':
        return 'Orthotic/Prosthetic Procedures'

      case 'M':
        return 'Medical Services'

      case 'P':
        return 'Pathology and Laboratory'

      case 'R':
        return 'Diagnostic Radiology Services'

      case 'V':
        return 'Vision/Hearing Services'
    }
  }

  return null
}
